import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SocketIoService } from 'src/app/core/services/socket-io/socket-io.service';
import { RequestFailure } from 'src/app/core/services/maintenance/failure-diagnostics';
import { GameInterface } from '../../interfaces';
import { AnnouncementType, CardColorType } from '../../types';

/**
 * Why a training request failed for good (retrying cannot help):
 * - 'replaced': another tab / device started a new training (409 trainingReplaced)
 * - 'gone': no training on the server any more (404 trainingNotFound: left, 6 h TTL,
 *   redis flushed; also a 404 of an older backend without the endpoint), or any other
 *   client error (400 / 403 / 422 ...) that a retry would only repeat.
 * null: transient (no connection, timeout, 5xx, 429) — and 401: the socket can carry a
 * stale session while the login is fine, so NPCGameService asks the server over HTTP
 * (`isLoggedIn`) whether the session really expired before ending the training.
 */
export type TrainingFailure = 'gone' | 'replaced' | null;

export function trainingFailure(error: unknown): TrainingFailure {
  const body = error instanceof RequestFailure ? error.body : error;
  if (body === 'trainingReplaced') return 'replaced';
  if (body === 'trainingNotFound' || body === 'noTrainingGame') return 'gone';
  if (!(error instanceof RequestFailure) || error.kind !== 'REJECTED') return null;
  const status = error.status ?? 0;
  if (status === 409) return 'replaced';
  if (status >= 400 && status < 500 && ![401, 408, 429].includes(status)) return 'gone';
  return null;
}

/** the server refused the request because the session is not logged in (any more) */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof RequestFailure && (error.kind === 'SESSION' || error.status === 401);
}

/**
 * Training games (vs. the bots). The server owns the game state (per user, in Redis):
 * every call sends only the player's choice and returns the current game, redacted to
 * what the player may see. Sent over the existing Sails socket instead of HTTP.
 * (The old stateless /game/npc/* endpoints remain on the server for cached clients.)
 */
@Injectable({
  providedIn: 'root',
})
export class NpcGameBackendService {
  private readonly BASE_ADDRESS = 'game/npc2';

  /**
   * uuid of the game this tab shows. Sent with every call so the server answers 409
   * instead of acting on a training another tab / device started meanwhile.
   */
  public gameUuid?: string;

  constructor(private socketIO: SocketIoService, private http: HttpClient) {}

  /** rejects with a RequestFailure carrying status + body, see `trainingFailure` */
  private post(action: string, data: any = {}, withGame = true): Promise<GameInterface> {
    const payload = withGame && this.gameUuid ? { ...data, gameUuid: this.gameUuid } : data;
    return this.socketIO.post(`${this.BASE_ADDRESS}/${action}`, payload, { withStatus: true });
  }

  /**
   * @description starts a new training game (replaces a running one on the server)
   * @param {number} numPlayers
   * @param {string} difficulty
   * @param {any} cardsOverride dev only, ignored by the server in production
   * @param {string} previousGameUuid only for the automatic next game: the game that just
   *   ended here. The server refuses it (409 trainingReplaced) when another tab / device
   *   runs a different game meanwhile. Omitted for the user's own start (always replaces).
   */
  public newGame(
    numPlayers: number,
    difficulty: 'easy' | 'medium' | 'hard' | 'expert' = 'medium',
    cardsOverride: any = undefined,
    previousGameUuid?: string
  ): Promise<GameInterface> {
    const data: any = { numPlayers, difficulty, cards: cardsOverride };
    if (previousGameUuid) data.previousGameUuid = previousGameUuid;
    return this.post('new', data, false);
  }

  /**
   * @description asks the server over HTTP (uncached, not over the socket, whose session
   * may be stale) whether this browser is still logged in; undefined when that is unknown
   * (no connection). A failed check never counts as logged out.
   */
  public async isLoggedIn(): Promise<boolean | undefined> {
    try {
      const session = await firstValueFrom(
        this.http
          .get<{ isLoggedIn: boolean }>(`${environment.API_SERVER_URL}/user/auth/is-logged-in`, {
            withCredentials: true,
            params: { _: Date.now() },
          })
          .pipe(timeout(10000))
      );
      return typeof session?.isLoggedIn === 'boolean' ? session.isLoggedIn : undefined;
    } catch {
      return undefined;
    }
  }

  /** @description current state of the training game (resync after a lost answer) */
  public getState(): Promise<GameInterface> {
    return this.post('state');
  }

  /** @description abandons the training game on the server (only `gameUuid`'s, if given) */
  public endGame(gameUuid: string | undefined = this.gameUuid): Promise<unknown> {
    return this.post('end', gameUuid ? { gameUuid } : {}, false);
  }

  /** @description lets the next bot act (no-op on the server while it is my turn) */
  public npcMove(): Promise<GameInterface> {
    return this.post('move');
  }

  public playCard(cardNumber: number): Promise<GameInterface> {
    return this.post('play-card', { cardNumber });
  }

  public giveCard(cardNumber: number): Promise<GameInterface> {
    return this.post('give-card', { cardNumber });
  }

  public marriage(cardNumber: number): Promise<GameInterface> {
    return this.post('marriage', { cardNumber });
  }

  public changeAtoutCard(): Promise<GameInterface> {
    return this.post('change-atout-card');
  }

  public zuadrahn(): Promise<GameInterface> {
    return this.post('zuadrahn');
  }

  public lookTalon(
    myCardsToGiveAway: number[],
    cardsIWantToHave: number[]
  ): Promise<GameInterface> {
    return this.post('look-talon', { myCardsToGiveAway, cardsIWantToHave });
  }

  public shout(color: CardColorType | undefined): Promise<GameInterface> {
    return this.post('shout', { color });
  }

  public announce(announcement: AnnouncementType): Promise<GameInterface> {
    return this.post('announce', { announcement });
  }

  public decideKontra(doContra: boolean, re: boolean): Promise<GameInterface> {
    return this.post('decide-kontra', { doContra, re });
  }
}
